import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Clock, CheckCircle2, Lock } from "lucide-react";
import * as Icons from "lucide-react";
import { SECTORS } from "@/lib/sectors";
import { HERO, SECTOR } from "@/constants/testIds";
import SectorGrid from "@/components/SectorGrid";
import HowItWorks from "@/components/HowItWorks";
import StatsCounter from "@/components/StatsCounter";
import Testimonials from "@/components/Testimonials";
import FAQAccordion from "@/components/FAQAccordion";
import TrustBar from "@/components/TrustBar";
import Seo, { organizationSchema, websiteSchema } from "@/components/Seo";

const QUICK_PICKS = ["pcp-motor-finance", "housing-disrepair", "personal-injury", "flight-delay-holiday"];

function Hero() {
    return (
        <section className="relative overflow-hidden bg-navy-900 text-white">
            {/* Background layers */}
            <div className="absolute inset-0 bg-mesh-emerald" />
            <div className="absolute inset-0 grid-bg-dark opacity-30" />
            <div
                className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-30"
                style={{ background: "radial-gradient(circle, #10b981 0%, transparent 70%)" }}
            />
            <div
                className="absolute -bottom-32 -left-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
                style={{ background: "radial-gradient(circle, #3b82f6 0%, transparent 70%)" }}
            />

            <div className="container-x relative pt-16 lg:pt-24 pb-20 lg:pb-28">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/[0.05] border border-white/10 backdrop-blur-sm px-3 py-1.5">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                            </span>
                            <span className="text-xs font-semibold tracking-wide text-emerald-200">FCA review live · PCP claims open</span>
                        </div>

                        <h1
                            data-testid={HERO.headline}
                            className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-display font-bold tracking-tight leading-[1.05]"
                        >
                            Claim what&apos;s
                            <br />
                            <span className="relative inline-block">
                                <span className="bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500 bg-clip-text text-transparent">
                                    rightfully yours.
                                </span>
                                <svg className="absolute -bottom-3 left-0 w-full" viewBox="0 0 300 12" preserveAspectRatio="none" fill="none">
                                    <path d="M2 9 Q150 -2 298 7" stroke="#10b981" strokeWidth="3" strokeLinecap="round" />
                                </svg>
                            </span>
                        </h1>

                        <p className="mt-7 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl">
                            The UK&apos;s trusted claims hub — covering PCP motor finance, housing disrepair, personal injury and 7 more sectors. Free check, No Win No Fee, paid in weeks not years.
                        </p>

                        <div className="mt-9 flex flex-wrap items-center gap-3">
                            <Link
                                to="/claim"
                                data-testid={HERO.primaryCta}
                                className="btn-primary text-base"
                            >
                                Start your free check
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/how-it-works"
                                data-testid={HERO.secondaryCta}
                                className="btn-ghost-dark text-base"
                            >
                                How it works
                            </Link>
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-slate-300">
                            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> No upfront fees</div>
                            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-400" /> 60-second check</div>
                            <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-emerald-400" /> GDPR-secure</div>
                            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> FCA aware</div>
                        </div>
                    </div>

                    {/* Right: quick claim picker card */}
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -inset-2 bg-gradient-to-tr from-emerald-500/30 to-blue-500/20 rounded-3xl blur-2xl" />
                            <div className="relative bg-white rounded-3xl border border-white/10 shadow-2xl p-7 lg:p-8">
                                <div className="flex items-center gap-2 text-emerald-700 text-xs font-bold uppercase tracking-[0.18em]">
                                    <Sparkles className="w-4 h-4" /> Start in seconds
                                </div>
                                <h3 className="mt-3 text-2xl font-display font-bold text-navy-900 leading-tight">
                                    What type of claim can we help with?
                                </h3>
                                <p className="mt-2 text-sm text-slate-500">Pick a category to launch your free eligibility check.</p>

                                <div data-testid={HERO.sectorPicker} className="mt-6 grid grid-cols-2 gap-3">
                                    {QUICK_PICKS.map((slug) => {
                                        const s = SECTORS.find((x) => x.slug === slug);
                                        const Icon = Icons[s.icon] || Icons.Scale;
                                        return (
                                            <Link
                                                key={slug}
                                                to={`/claim?sector=${slug}`}
                                                data-testid={SECTOR.startClaim(slug)}
                                                className="group p-4 rounded-2xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 transition-all"
                                            >
                                                <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 group-hover:border-emerald-600 transition-colors">
                                                    <Icon className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                                                </div>
                                                <div className="mt-3 text-sm font-semibold text-navy-900 leading-tight">{s.name}</div>
                                                <div className="mt-1 text-[11px] text-slate-500">{s.averagePayout}</div>
                                            </Link>
                                        );
                                    })}
                                </div>

                                <Link to="/claim" className="mt-5 w-full btn-primary text-sm">
                                    See all 10 claim types <ArrowRight className="w-4 h-4" />
                                </Link>

                                <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3 text-xs text-slate-500">
                                    <div className="flex -space-x-2">
                                        {["A", "M", "S", "K"].map((c, i) => (
                                            <div key={c} className={`w-7 h-7 rounded-full border-2 border-white flex items-center justify-center font-bold text-[10px] ${["bg-emerald-100 text-emerald-700", "bg-amber-100 text-amber-700", "bg-blue-100 text-blue-700", "bg-fuchsia-100 text-fuchsia-700"][i]}`}>{c}</div>
                                        ))}
                                    </div>
                                    <div>
                                        <span className="font-semibold text-navy-900">47,800+ </span>
                                        UK claimants helped to date
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ValueProps() {
    const items = [
        { title: "Specialist solicitors, not call-centre staff", desc: "Every case is matched with an SRA-regulated specialist firm in the relevant practice area. No generic templates, no junior handover." },
        { title: "Truly No Win, No Fee — capped by the FCA", desc: "You pay zero upfront. Our success fee is capped within FCA limits and disclosed in writing before any claim begins. Lose? You pay nothing." },
        { title: "Bank-grade data security", desc: "ICO-registered, GDPR-by-default, encrypted in transit and at rest in UK data centres. We never sell your details. Full stop." },
        { title: "Updates the day they happen", desc: "Track your claim in real-time via your client portal — and we&apos;ll text you the moment anything changes. No waiting on hold." },
    ];
    return (
        <section className="section-pad bg-white">
            <div className="container-x">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-28">
                        <span className="text-eyebrow">Why ClaimHub UK</span>
                        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-navy-900">
                            A claims hub built for the way Britain actually claims today.
                        </h2>
                        <p className="mt-5 text-base text-slate-600 leading-relaxed">
                            Most claim companies haven&apos;t updated in a decade. We rebuilt the entire process — mobile-first, transparent, FCA-aware and powered by specialist UK lawyers.
                        </p>
                        <Link to="/about" className="mt-6 inline-flex items-center gap-1.5 text-emerald-700 font-semibold hover:gap-2.5 transition-all">
                            More about us <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                        {items.map((it) => (
                            <div key={it.title} className="card-soft card-soft-hover p-7">
                                <div className="w-9 h-9 rounded-xl bg-navy-900 flex items-center justify-center">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                </div>
                                <h3 className="mt-5 text-lg font-display font-semibold text-navy-900 leading-tight">{it.title}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function FinalCta() {
    return (
        <section className="bg-slate-50">
            <div className="container-x py-20 lg:py-28">
                <div className="relative overflow-hidden rounded-3xl bg-navy-900 text-white p-10 lg:p-16">
                    <div className="absolute inset-0 bg-mesh-emerald opacity-90" />
                    <div className="absolute inset-0 grid-bg-dark opacity-25" />
                    <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <span className="text-eyebrow-light">Ready when you are</span>
                            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">
                                You&apos;ve got 60 seconds. <br />
                                We&apos;ve got <span className="text-emerald-400">£218m+</span> of proof.
                            </h2>
                            <p className="mt-5 text-base text-slate-300 max-w-xl">
                                Start your free, no-obligation eligibility check now. If you don&apos;t qualify, we&apos;ll tell you straight. If you do, we&apos;ll get to work today.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row lg:justify-end gap-3">
                            <Link to="/claim" className="btn-primary text-base">
                                Start free check <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/contact" className="btn-ghost-dark text-base">
                                Speak to our team
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <Seo
                title=""
                description="ClaimHub UK — the UK's trusted hub for PCP & motor finance, housing disrepair, personal injury and 7 more sectors. Free 60-second check, No Win No Fee, paid in weeks not years."
                path="/"
                jsonLd={[organizationSchema(), websiteSchema()]}
            />
            <Hero />
            <TrustBar />
            <SectorGrid />
            <HowItWorks />
            <StatsCounter />
            <ValueProps />
            <Testimonials />
            <FAQAccordion />
            <FinalCta />
        </>
    );
}
